import type { Organization, Patient, Reference, RelatedPerson } from "@medplum/fhirtypes";

import { StatementStatus } from "@/billing/components/StatementsTab/constants/statements";
import { SERVICE_PAYER_CODE } from "@/billing/constants/service-payer-code";

import type { ReferenceDisplayCache } from "./reference-displays";

export type ServicePayerCode = (typeof SERVICE_PAYER_CODE)[keyof typeof SERVICE_PAYER_CODE];

/** Resolved payer names keyed by `Invoice.recipient.reference`. */
export type PayerDisplayCache = ReferenceDisplayCache;

export interface OpenStatementsRow {
  /** The id of the Invoice the row was reduced to, and the row key. */
  id: string;
  clientId: string;
  clientName: string;
  medicalRecordNumber: string;
  /** `Invoice.date` — the day the statement was billed. */
  serviceDate: string | undefined;
  charges: number | undefined;
  dueFrom: string;
  status: StatementStatus;
  servicePayerCode: ServicePayerCode | undefined;
  /**
   * `Invoice.recipient` — who the statement is billed to: the payer Organization for insurance, the
   * client Patient for self-pay. Kept as a reference rather than a resolved name because the Payer
   * search resolves displays lazily, only once the user searches by payer.
   */
  payerReference: Reference<Organization | Patient | RelatedPerson> | undefined;
  /** Sum of the payments posted against the statement; 0 when nothing is posted. */
  payments: number;
  /** Signed sum of the adjustments posted against the statement; 0 when nothing is posted. */
  adjustments: number;
  /** What is still owed. Rows where this is 0 never reach the table. */
  balance: number | undefined;
}

export interface OpenStatementsFilters {
  statuses: StatementStatus[];
}

export type LoadOpenStatementsResult =
  | { ok: true; data: OpenStatementsRow[] }
  | { ok: false; reason: "missing_practice_id" | "load_failed"; details?: string };

/** Which field the Open Statements search box matches on. */
export enum OpenStatementsSearchOption {
  Client = "client",
  Payer = "payer",
}
